import {
    ApplicationRef,
    ComponentFactoryResolver,
    ComponentRef,
    EmbeddedViewRef,
    Injectable,
    Injector,
} from '@angular/core';
import {ModalService} from './modal/modal.service';
import {AlertService} from './notify/alert.service';
import {Router} from '@angular/router';
import {MemberModel} from '../models/user.model';
import {CurrencyPipe, DatePipe, TitleCasePipe} from '@angular/common';
import {ContractStepModel} from '../models/contract.model';
import {MemberService} from './member.service';
import {AuthService} from './api/auth.service';
import {DocumentsServiceService} from '../controllers/documents/documents-service.service';
import {ProfileCardModalComponent} from '../components/modal/profile-card-modal/profile-card-modal.component';
import {TranslateService} from '@ngx-translate/core';

export type ComponentType<T> = new (...args: any[]) => T;

@Injectable({
    providedIn: 'root',
})

export class HelpersService {
    private components: ComponentRef<any>[] = [];

    constructor(
        public controller: DocumentsServiceService,
        protected modalService: ModalService,
        protected alertService: AlertService,
        protected authApi: AuthService,
        protected router: Router,
        protected datePipe: DatePipe,
        protected memberService: MemberService,
        private appRef: ApplicationRef,
        private resolver: ComponentFactoryResolver,
        private injector: Injector,
        private translate: TranslateService,
        private currencyPipe:CurrencyPipe
    ) {
    }

    add<T>(
        component: ComponentType<T> | ComponentRef<T>,
        element?: Element | string,
    ): ComponentRef<T> {
        const componentRef = component instanceof ComponentRef
            ? component
            : this.resolver.resolveComponentFactory(component).create(this.injector);
        this.appRef.attachView(componentRef.hostView);
        if (typeof element === 'string') {
            element = document.querySelector(element);
        }
        if (!element) {
            element = document.body;
        }
        element.appendChild(
            (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement,
        );
        this.components.push(componentRef);
        return componentRef;
    }

    dateFromFormat(str: string, format: string = 'dd.mm.yyyy'): Date {
        if (!str) {
            return;
        }
        const parts: string[] = str.match(/(\d+)/g);
        const fmt: any = {dd: 0, mm: 0, yyyy: 0};
        let i = 0;
        format.replace(/(dd|mm|yyyy)/g, (part: string): any => {
            fmt[part] = i++;
        });
        return new Date(parseInt(parts[fmt.dd], 10), parseInt(parts[fmt.mm], 10) - 1, parseInt(parts[fmt.yyyy], 10));
    }

    /**
     * Форматирование даты
     * @param date - Дата в форме таймштампа базы
     * @param isTime - выводить время
     * @param formatGmt
     */
    formatDate(date, isTime = true, formatGmt = false): string {
        let newDate = new Date(date);
        if (newDate.toString() === 'Invalid Date') {
            newDate = this.dateFromFormat(date);
        }
        if (formatGmt) {
            newDate = new Date(newDate.setHours(newDate.getHours() - 3));
        }
        return this.datePipe.transform(newDate, 'dd.MM.yyyy' + (isTime ? ' HH:mm' : ''));
    }

    chatDate(date): string {
        const dates = new Date(date);
        const today = new Date();
        if (today.getUTCDate() === dates.getUTCDate()) {
            return this.datePipe.transform(dates, 'HH:mm');
        } else {
            return this.formatDate(date, true);
        }
    }

    /**
     * Отправка инвайта
     * @param inviteData
     * {email: string, observ: string (outer, inner. depadmin, projadmin)}
     */
    async setInvite(inviteData): Promise<any> {
        return new Promise((resolve, reject) => {
            const sub = this.authApi.inviteUserInProject(inviteData).subscribe({
                next: data => {
                    this.alertService.setAlert(data.message, 'green');
                    resolve(data);
                    sub.unsubscribe();
                }, error: error => {
                    this.alertService.setAlert(error.message, 'blue');
                    reject(error.response);
                    sub.unsubscribe();
                },
            });
        });
    }

    /**
     * Вывод полного имени с аббревиатурами
     * @param user
     * @param full - обрезать имя или нет
     */
    shortFullName(user: MemberModel, full?: boolean): string {
        let fullName;

        if (user.is_active) {
            fullName = [user.lastname, user.firstname, user.middlename].filter(y => y);
            if (!fullName.length) {
                fullName = [user.email];
            }
            for (let i = 1; i <= fullName.length; i++) {
                if (fullName[i]) {
                    fullName[i] = fullName[i].charAt(0) + '.';
                }
            }
        } else {
            if (full) {
                fullName = [user.email.substring(0, 15) + '...'];
            } else {
                fullName = [user.email];
            }
        }

        return fullName.join(' ');
    }

    /**
     * Вывод полного имени
     * @param user
     */
    fullName(user: MemberModel): string | null {
        let fullName = [user?.lastname, user?.firstname, user?.middlename].filter(x => x);
        if (!fullName.length) {
            fullName = [user?.email];
        }
        return fullName.join(' ');
    }

    /**
     * Открытие карточки профиля по guid пользователя
     * @param guid
     */
    public openProfileByGuid(guid): void {
        ProfileCardModalComponent.prototype.memberGuid = guid;
        this.add(ProfileCardModalComponent);
    }

    /**
     * Генератор названия этапа контракта
     * @param contractStep
     * @param element
     */
    defaultContractStepName(contractStep: ContractStepModel, element:any = null): string {
        let name = '';

        switch (contractStep.target_type) {
            case 'file':
                if (element) {
                    name = this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.FILE', {name: element.name});
                } else {
                    name = this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.FILE', {name: ''});
                }
                break;

            case 'task':
                if (element) {
                    name = this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.TASK', {name: element.title});
                } else {
                    name = this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.TASK', {name: ''});
                }
                break;

            case 'payment':
                if (element) {
                    name = this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.PAYMENT', {total: this.transformToCurrency(element.total)});
                } else {
                    name = this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.PAYMENT', {total: ''});
                }
                break;

            case 'sub_contract':
                if (element) {
                    name = this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.CONTRACT', {name: element.contract.name});
                } else {
                    name = this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.CONTRACT', {name: ''});
                }
                break;

            case 'credit_letter':
            case 'general_credit_letter':
                name = this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.CREDIT_LETTER.START');
                if (contractStep.target.payer_project) {
                    name += this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.CREDIT_LETTER.PROJECT',
                        {name: contractStep.target.payer_project.project.name});
                }
                if (element) {
                    name += this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.CREDIT_LETTER.TOTAL',
                        {total: this.transformToCurrency(element.total)});
                }
                break;

            case 'multi_payment':
                name = this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.MULTIPAYMENT');
                if (element && element.paymentsData?.length) {
                    for (const paymentData of element.paymentsData) {
                        if (element.paymentsData.indexOf(paymentData) !== element.paymentsData?.length - 1) {
                            name += this.transformToCurrency(paymentData.payment.total) + ',  ';
                        } else {
                            name += this.transformToCurrency(paymentData.payment.total);
                        }
                    }
                }
                break;

            case 'direct_payment':
                if (element) {
                    name = this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.DIRECT_PAYMENT', {total: this.transformToCurrency(element.total)});
                } else {
                    name = this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.DIRECT_PAYMENT', {total: ''});
                }
                break;
        }
        if (!element) {
            name += this.translate.instant('CONTRACTS.TEXT.STEPS_NAME.EMPTY');
        }

        return name;
    }
    transformToCurrency (data:string):string {
      return this.currencyPipe.transform(data,'RUB','symbol-narrow')||data
    }

    translit(word): string {
        let answer = '';
        const converter = {
            'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd',
            'е': 'e', 'ё': 'e', 'ж': 'zh', 'з': 'z', 'и': 'i',
            'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm', 'н': 'n',
            'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't',
            'у': 'u', 'ф': 'f', 'х': 'h', 'ц': 'c', 'ч': 'ch',
            'ш': 'sh', 'щ': 'sch', 'ь': '', 'ы': 'y', 'ъ': '',
            'э': 'e', 'ю': 'yu', 'я': 'ya',
            'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'G', 'Д': 'D',
            'Е': 'E', 'Ё': 'E', 'Ж': 'Zh', 'З': 'Z', 'И': 'I',
            'Й': 'Y', 'К': 'K', 'Л': 'L', 'М': 'M', 'Н': 'N',
            'О': 'O', 'П': 'P', 'Р': 'R', 'С': 'S', 'Т': 'T',
            'У': 'U', 'Ф': 'F', 'Х': 'H', 'Ц': 'C', 'Ч': 'Ch',
            'Ш': 'Sh', 'Щ': 'Sch', 'Ь': '', 'Ы': 'Y', 'Ъ': '',
            'Э': 'E', 'Ю': 'Yu', 'Я': 'Ya',
        };
        for (const item of word) {
            if (converter[item] === undefined) {
                answer += item;
            } else {
                answer += converter[item];
            }
        }
        return answer;
    }

    generateDashboardAlias(label: string): string {
        const name = label ? label.split(' ') : '';
        let alias = '';
        if (name.length > 1) {
            for (const element of name) {
                alias = alias + element.replace(/[^\sa-zA-ZЁёА-я0-9]/g, '').substring(0, 1);
            }
            return this.translit(alias).toUpperCase();
        } else {
            alias = label.replace(/[^\sa-zA-ZЁёА-я0-9]/g, '').substring(0, 3);
            return this.translit(alias).toUpperCase();
        }
    }


    getTimeFromMinute(minute: any, isDate = false): string {
        minute = Number(minute);
        const day = Math.trunc(minute / 1440);
        const hours = Math.trunc((minute - day * 1440) / 60);
        const minutes = (minute % 60).toString().split('.')[0];
        if (day === 0 && hours === 0 && minutes === '0') {
            return 'Менее минуты назад';
        }
        let time: string = (day ? day + ' д. ' : '') + (hours ? hours + ' ч. ' : '') + minutes + ' мин.';

        if (isDate) {
            time = time + ' назад';
        }

        return time;
    }

    convertDateToTime(date): string {
        const today: Date = new Date();
        const createDay: Date = new Date(date);

        if (today.getFullYear() !== createDay.getFullYear()
            || today.getMonth() !== createDay.getMonth()
            || today.getDay() !== createDay.getDay()
        ) {
            return this.datePipe.transform(date, 'HH:mm dd.MM.yyyy');
        }

        return this.getTimeFromMinute(((today.getTime() - createDay.getTime()) / (60 * 1000)).toString().split('.')[0], true);
    }
}
